import {Kpi, KpiResponse, KpiProcessStage} from '@hconnect/common/types'
import MockAdapter from 'axios-mock-adapter'
import {cloneDeep} from 'lodash'

import {KpiPayload} from '../../modules/plant-setup/plant-setup-tabs/kpis/types'
import {mockStore} from '../mockStore'

import {numberRegEx, sleepResponse, saveScenario, uuidRegEx} from './utils'

export const enableKpisEndpoints = (mock: MockAdapter) => {
  // matches plant-wide `/kpis` and node-scoped `/kpis/{upmId}` (department/area)
  mock
    .onGet(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis(/${uuidRegEx})?$`))
    .reply((config) => {
      const {
        burglengenfeld: {kpis}
      } = mockStore.scenario()

      const search = (config.params as {search?: string})?.search

      let data: KpiResponse = kpis
      if (search) {
        const lower = search.toLowerCase()
        data = {
          ...data,
          processStages: data.processStages.map((stage) => ({
            ...stage,
            definitionsWithResults: stage.definitionsWithResults.filter((kpi) =>
              kpi.definition.displayName.toLowerCase().includes(lower)
            )
          }))
        }
      }
      return sleepResponse([200, data])
    })

  mock.onGet(new RegExp(`^/kpieditor/plants/${numberRegEx}/sensors$`)).reply(() => {
    const {
      burglengenfeld: {sensors}
    } = mockStore.scenario()

    return sleepResponse([200, sensors])
  })

  mock
    .onGet(
      new RegExp(
        `/janus-kpi/janus-specific-kpi/${uuidRegEx}(\\?upmId=${uuidRegEx}&upmMappingType=1&unitTypeId=(\\d+|null))?$`
      )
    )
    .reply(() => {
      const {
        burglengenfeld: {janusSpecificKpis}
      } = mockStore.scenario()

      return sleepResponse([200, janusSpecificKpis])
    })

  mock.onGet(new RegExp(`/janus-kpi/janus-specific-kpi/${uuidRegEx}/by-definition$`)).reply(() => {
    const {
      burglengenfeld: {specificKpisByDefinition}
    } = mockStore.scenario()

    return sleepResponse([200, specificKpisByDefinition])
  })

  mock.onGet(new RegExp(`^/kpieditor/plants/${numberRegEx}/functions$`)).reply(() => {
    const {
      burglengenfeld: {functions}
    } = mockStore.scenario()

    return sleepResponse([200, functions])
  })

  mock
    .onGet(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/units$`))
    .reply(() => {
      const {
        burglengenfeld: {kpiUnits}
      } = mockStore.scenario()

      return sleepResponse([200, kpiUnits])
    })

  mock.onGet(new RegExp('^/kpieditor/plants/kpiunits')).reply(() => {
    const {
      burglengenfeld: {availableKpiUnits}
    } = mockStore.scenario()

    return sleepResponse([200, availableKpiUnits])
  })

  mock.onGet(new RegExp('^/kpieditor/plants/kpinames')).reply(() => {
    const {
      burglengenfeld: {kpiNames}
    } = mockStore.scenario()

    return sleepResponse([200, kpiNames])
  })

  // POST create kpi
  mock.onPost(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis$`)).reply((config) => {
    const kpiDto = JSON.parse(config.data as string) as KpiPayload
    const {formula, aggregationType, weightKpiAssignmentId, unit, kpiTagName, kpiType} = kpiDto
    const scenario = mockStore.scenario()
    const newKpi: Kpi = {
      kpiDefinitionId:
        scenario.burglengenfeld.kpis.processStages[0].definitionsWithResults.length + 1,
      formula,
      tagname: kpiTagName,
      displayName: kpiType,
      unit,
      kpiVisibilityFlags: [],
      kpiAssignment: {
        id: scenario.burglengenfeld.kpis.processStages[0].definitionsWithResults.length + 1,
        aggregationType,
        weightAssignmentId: weightKpiAssignmentId || ''
      }
    }

    scenario.burglengenfeld.kpis.processStages[0].definitionsWithResults.push({
      definition: newKpi
    })

    saveScenario(scenario)
    return sleepResponse([201, newKpi])
  })

  const modifyKPI = (processStages: KpiProcessStage[], kpiDto: Kpi, inputKpiId: number) => {
    const editedProcessStages = cloneDeep(processStages)
    for (const processStage of editedProcessStages) {
      const matchedKpiIndex = processStage.definitionsWithResults.findIndex(
        (kpi) => kpi.definition.kpiDefinitionId === inputKpiId
      )
      if (matchedKpiIndex >= 0) {
        processStage.definitionsWithResults[matchedKpiIndex] = {
          ...processStage.definitionsWithResults[matchedKpiIndex],
          ...kpiDto
        }
        return editedProcessStages
      }
    }
    return editedProcessStages
  }

  // PATCH edit kpi display name
  mock
    .onPatch(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/displayname$`))
    .reply((config) => {
      const kpiId = Number((config.url as string).split('/')[5])
      const kpiDto = JSON.parse(config.data as string) as Kpi
      const scenario = mockStore.scenario()

      const editedProcessStages = modifyKPI(
        scenario.burglengenfeld.kpis.processStages,
        kpiDto,
        kpiId
      )

      scenario.burglengenfeld.kpis.processStages = editedProcessStages
      saveScenario(scenario)
      return sleepResponse([200, {}])
    })

  mock
    .onPatch(new RegExp(`janus-kpi/janus-specific-kpi/${uuidRegEx}/${uuidRegEx}$`))
    .reply((config) => {
      const kpiId = (config.url as string).split('/')[4]

      const scenario = mockStore.scenario()

      scenario.burglengenfeld.specificKpisByDefinition =
        scenario.burglengenfeld.specificKpisByDefinition.filter((kpi) => kpi.id !== kpiId)

      saveScenario(scenario)
      return sleepResponse([200, {}])
    })

  // DELETE kpi
  mock
    .onDelete(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}$`))
    .reply((config) => {
      const kpiId = Number((config.url as string).split('/')[5])
      const scenario = mockStore.scenario()

      // Remove KPI from all process stages
      scenario.burglengenfeld.kpis.processStages.forEach((stage) => {
        stage.definitionsWithResults = stage.definitionsWithResults.filter(
          (kpi) => kpi.definition.kpiDefinitionId !== kpiId
        )
      })

      saveScenario(scenario)
      return sleepResponse([200, {}])
    })
  // PATCH edit kpi display name
  mock
    .onPatch(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/formula$`))
    .reply((config) => {
      const kpiId = Number((config.url as string).split('/')[5])
      const kpiDto = JSON.parse(config.data as string) as Kpi
      const scenario = mockStore.scenario()

      const editedProcessStages = modifyKPI(
        scenario.burglengenfeld.kpis.processStages,
        kpiDto,
        kpiId
      )

      scenario.burglengenfeld.kpis.processStages = editedProcessStages
      saveScenario(scenario)
      return sleepResponse([200, {}])
    })

  // PATCH edit kpi unit
  mock
    .onPatch(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/unit$`))
    .reply((config) => {
      const kpiId = Number((config.url as string).split('/')[5])
      const kpiDto = JSON.parse(config.data as string) as Kpi
      const scenario = mockStore.scenario()

      const editedProcessStages = modifyKPI(
        scenario.burglengenfeld.kpis.processStages,
        kpiDto,
        kpiId
      )

      scenario.burglengenfeld.kpis.processStages = editedProcessStages
      saveScenario(scenario)
      return sleepResponse([200, {}])
    })

  // PATCH edit kpi aggregation type
  mock
    .onPatch(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/aggregation-type$`))
    .reply((config) => {
      const kpiId = Number((config.url as string).split('/')[5])
      const kpiDto = JSON.parse(config.data as string) as Kpi
      const scenario = mockStore.scenario()

      const editedProcessStages = modifyKPI(
        scenario.burglengenfeld.kpis.processStages,
        kpiDto,
        kpiId
      )

      scenario.burglengenfeld.kpis.processStages = editedProcessStages
      saveScenario(scenario)
      return sleepResponse([200, {}])
    })

  // PATCH edit kpi weight
  mock
    .onPatch(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/weight-kpi$`))
    .reply((config) => {
      const kpiId = Number((config.url as string).split('/')[5])
      const kpiDto = JSON.parse(config.data as string) as Kpi
      const scenario = mockStore.scenario()

      const editedProcessStages = modifyKPI(
        scenario.burglengenfeld.kpis.processStages,
        kpiDto,
        kpiId
      )

      scenario.burglengenfeld.kpis.processStages = editedProcessStages
      saveScenario(scenario)
      return sleepResponse([200, {}])
    })

  // PATCH edit kpi time step
  mock
    .onPatch(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/frequency$`))
    .reply((config) => {
      const kpiId = Number((config.url as string).split('/')[5])
      const kpiDto = JSON.parse(config.data as string) as Kpi
      const scenario = mockStore.scenario()

      const editedProcessStages = modifyKPI(
        scenario.burglengenfeld.kpis.processStages,
        kpiDto,
        kpiId
      )

      scenario.burglengenfeld.kpis.processStages = editedProcessStages
      saveScenario(scenario)
      return sleepResponse([200, {}])
    })

  // PATCH edit kpi visibility
  mock
    .onPatch(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/visibility$`))
    .reply((config) => {
      const kpiId = Number((config.url as string).split('/')[5])
      const kpiVisibilityFlags = JSON.parse(config.data as string)
      const kpiDto = {kpiVisibilityFlags} as Kpi
      const scenario = mockStore.scenario()

      const editedProcessStages = modifyKPI(
        scenario.burglengenfeld.kpis.processStages,
        kpiDto,
        kpiId
      )

      scenario.burglengenfeld.kpis.processStages = editedProcessStages
      saveScenario(scenario)
      return sleepResponse([200, {}])
    })

  // PATCH edit kpi thresholds
  mock
    .onPatch(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/ranges$`))
    .reply((config) => {
      const kpiId = Number((config.url as string).split('/')[5])
      const kpiDto = JSON.parse(config.data as string) as Kpi
      const scenario = mockStore.scenario()

      const editedProcessStages = modifyKPI(
        scenario.burglengenfeld.kpis.processStages,
        kpiDto,
        kpiId
      )

      scenario.burglengenfeld.kpis.processStages = editedProcessStages
      saveScenario(scenario)
      return sleepResponse([200, {}])
    })

  mock
    .onGet(new RegExp(`^/kpieditor/plants/${numberRegEx}/kpis/${numberRegEx}/last-results$`))
    .reply(() => {
      const {
        burglengenfeld: {lastResults}
      } = mockStore.scenario()

      return sleepResponse([200, lastResults])
    })
  mock.onGet(new RegExp(`^/kpi-result/${numberRegEx}/validate-kpi$`)).reply(() => {
    const {
      burglengenfeld: {lastResults}
    } = mockStore.scenario()
    return sleepResponse([200, lastResults])
  })
}
